<template>
  <div
    v-if="hasVisible"
    ref="quickNavRef"
    class="quick-nav-button"
    :class="{ 'quick-nav-button--opened': isOpened }"
  >
    <BaseButtonIcon
      icon-name="vuesax:category-2-linear"
      icon-size="20px"
      variant="primary"
      size="md-alt"
      outlined
      class="quick-nav-button__control"
      @click="isOpened = !isOpened"
    />
    <div class="quick-nav-button__expansion">
      <BaseButtonIcon
        icon-name="vuesax:messages-2-linear"
        icon-size="20px"
        variant="primary"
        size="md-alt"
        outlined
        @click="handleClickNav()"
      />
      <BaseButtonIcon
        icon-name="app:logo"
        icon-size="20px"
        icon-fill-type="solid"
        variant="primary"
        size="md-alt"
        outlined
        @click="handleClickNav(permissions.WORKSPACE.ROUTE)"
      />
      <BaseButtonIcon
        icon-name="vuesax:notification"
        icon-size="20px"
        variant="primary"
        size="md-alt"
        outlined
        @click="handleClickNav(permissions.NOTIFICATIONS.ROUTE)"
      />
      <BaseButtonIcon
        icon-name="vuesax:profile-circle-linear"
        icon-size="20px"
        variant="primary"
        size="md-alt"
        outlined
        @click="handleClickNav(permissions.LK.PROFILE.ROUTE)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { permissions } from '~/shared/lib'
import { BaseButtonIcon } from '~/shared/ui'

const route = useRoute()

const quickNavRef = ref<HTMLElement | null>(null)
const isOpened = ref(false)

const hasVisible = computed(
  () =>
    ![
      permissions.AUTH.PERMISSION,
      permissions.AUTH.SIGNIN.PERMISSION,
      permissions.AUTH.SIGNUP.PERMISSION,
      permissions.AUTH.VERIFY.PERMISSION,
      permissions.AUTH.PROFILE.PERMISSION,
      permissions.AUTH.SET_PASSWORD.PERMISSION,
      permissions.AUTH.FORGOT_PASSWORD.PERMISSION,
      permissions.AUTH.FORGOT_PASSWORD.RESET.PERMISSION,
      permissions.AUTH.FORGOT_PASSWORD.VERIFY.PERMISSION,
      permissions.AUTH.FORGOT_PASSWORD.SUCCESS.PERMISSION,
      permissions.WORKSPACE.PERMISSION,
      permissions.LK.PROFILE.PERMISSION,
      permissions.ADMIN.PERMISSIONS.PERMISSION,
      permissions.NEWS.COMMENTS.PERMISSION,
      permissions.WORKSPACE.CALENDAR.TASK_COMMENTS.PERMISSION,
      permissions.WORKSPACE.CALENDAR.TASK_INVITATIONS.PERMISSION,
    ].includes(route.name as string),
)

function handleClickNav(route?: string) {
  if (route) {
    navigateTo(route)
  }

  isOpened.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node
  if (quickNavRef.value && !quickNavRef.value.contains(target)) {
    isOpened.value = false
  }
}
</script>

<style lang="scss" scoped>
.quick-nav-button {
  position: fixed;
  z-index: 999999;
  bottom: rem(64px);
  right: rem(64px);

  @include bp-media('xs', 'sm') {
    bottom: rem(44px);
    right: rem(36px);
  }

  @include bp-media('xs') {
    bottom: rem(32px);
    right: rem(32px);
  }

  &__control {
    box-shadow: 0 0 12px #0963f233;
    position: relative;
    z-index: 2;
  }

  &__expansion {
    z-index: 1;
    transform-origin: left center;
    transform: scaleX(0) translateX(-50%);
    opacity: 0;
    transition:
      transform 0.2s ease,
      opacity 0.2s ease;
    position: absolute;
    left: rem(-8px);
    top: 0;
    height: 100%;
    display: flex;
    background-color: var(--color-surface-bg);
    border-radius: var(--radius-xs);
    box-shadow: 0 0 12px #0963f233;

    &:deep(> *) {
      border-width: 0 !important;
    }
  }

  &--opened &__expansion {
    transform: scaleX(1) translateX(-100%);
    opacity: 1;
  }
}
</style>
